import { Colour } from '@insights-ltd/design-library/components';
import type { Pronoun } from 'domain/pronouns';
import { Dialects, PreferredDialect } from 'types/dialects';
import { ReasonToArchive } from 'api/archive/archiveApiTypes';
import {
  Chapters as Chapter,
  Contributor,
  Evaluator,
  EventType,
  Frequency,
  HasStatus,
  InviteeColorScores,
  InviteeListOptions,
  JungianColours,
  LicensedProduct,
  ProfileType,
  Role,
} from 'types/types';
import { OrganisationVisibility } from 'api/organisations/organisationApitypes';
import { ChapterName } from 'components/PurchaseProfiles/types';
import { InfiniteData } from '@tanstack/react-query';
import { QueryParamsBase } from 'api/search';

export interface RequestErrorBody {
  errors: { code: string }[];
  message: string;
  status: number;
  ts: string;
}

export class RequestError extends Error {
  readonly errorCodes: string[];

  readonly status: number;

  getRequestErrorKeys = (
    defaultKey: string,
    httpCodeKeys: Record<number, string> = {},
    errorCodeKeys: Record<string, string> = {},
  ) => {
    const genericError = httpCodeKeys[this.status]
      ? [httpCodeKeys[this.status]]
      : null;
    const specificErrorStrings = this.errorCodes.reduce(
      (accumulator: string[], currCode: string) => {
        if (errorCodeKeys[currCode]) {
          accumulator.push(errorCodeKeys[currCode]);
        }
        return accumulator;
      },
      [],
    );
    if (specificErrorStrings && specificErrorStrings.length) {
      return specificErrorStrings;
    }
    if (genericError) {
      return genericError;
    }
    return [defaultKey];
  };

  constructor(message: string, responseBody: RequestErrorBody) {
    // 'Error' breaks prototype chain here in ES5
    super(message);
    // restore prototype chain
    Object.setPrototypeOf(this, RequestError.prototype);

    this.status = responseBody.status;
    this.errorCodes = responseBody.errors.map((codeObj) => codeObj.code);
  }
}

export interface MutationResponse {
  id: string;
}

export interface RemoteLocation {
  type: 'REMOTE';
  meetingLink: string;
  details: string;
}

export interface PhysicalLocation {
  type: 'PHYSICAL';
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  state: string;
  country: string;
}

export type EventLocation = RemoteLocation | PhysicalLocation;

export interface IdObject {
  id: string;
}

export interface EventRequest {
  name: string;
  eventType: string;
  productType: LicensedProduct;
  dialect: string;
  timezone: string;
  startsAt: string;
  endsAt: string;
  deadlineAt?: string;
  notificationFrequency: string;
  location: EventLocation;
  practitioners: IdObject[];
  organisation?: OrganisationResponse;
  customEmailMessage: string;
}

export interface PurchaseLearnerChapterRequest {
  product: string;
  createProfile: {
    evaluations: string[];
    chapters: string[];
  };
  updateProfile: {
    profile: string;
    chapters: string[];
  };
}

export interface ProfilePurchaseLine {
  profile: string;
  chapters: string[];
}

export interface LearnerPurchaseLine {
  learner: string;
  evaluations: string[];
  chapters: string[];
}

export interface LearnerPurchaseRequest {
  product: string;
  createProfiles: LearnerPurchaseLine[];
  updateProfiles: ProfilePurchaseLine[];
}

export interface ChapterCost {
  name: ChapterName;
  cost: number;
}

export type DistributionFormat =
  'ATTACHMENT' | 'PROFILE_LINK' | 'DIGITAL_PROFILE_LINK';

export interface EventResponse {
  id: string;
  name: string;
  eventType: EventType;
  eventStatus: 'ACTIVE' | 'CANCELLED';
  productType: LicensedProduct;
  dialect: string;
  timezone: string;
  startsAt: string;
  endsAt: string;
  extendedEndDate: string;
  deadlineAt: string;
  notificationFrequency: Frequency | '';
  location: EventLocation;
  practitioners: PractitionerData[];
  organisation?: OrganisationResponse;
  cancelledAt?: string;
  customEmailMessage?: string;
  budget?: EventBudget;
  shareProfilesAt?: string;
  shareProfilesDistributionType?: DistributionFormat;
}

export interface EventBudget {
  total: number;
  potentialSpend: number;
  spent: number;
  recoverable: number;
}

interface Chapters {
  chapters: ChapterCost[];
}

export interface EventResponseWithChapters extends EventResponse, Chapters {}

export interface EventSummaryResponse {
  id: string;
  name: string;
  eventType: EventType;
  startsAt: string;
  endsAt: string;
  extendedEndDate: string;
  timezone: string;
  organisation?: OrganisationResponse;
  cancelledAt?: string | null;
}

export type ProfileStatus =
  'NONE' | 'REUSED' | 'PURCHASED' | 'AVAILABLE_FOR_REUSE';

export type ProfileReuseStatus = 'NONE' | 'REQUEST' | 'DECLINE';

export type ChapterStatus = 'PURCHASED' | 'PENDING';

export interface ChaptersStatus {
  name: string;
  status?: ChapterStatus;
}

export interface EvaluationStatus {
  submittedOn: string;
}

export type InviteeProfile = {
  id: string;
  status: ProfileStatus;
  createdAt?: string;
  reusePreference: ProfileReuseStatus;
};

export interface InviteeResponse extends HasStatus {
  id: string;
  email: string;
  fullName: string;
  status: InviteeListOptions;
  chapters: ChaptersStatus[];
  dialect: Dialects;
  lastInviteAt?: string;
  profileLastSentAt?: string;
  leadingColourEnergy?: JungianColours;
  evaluation?: EvaluationStatus;
  pronoun?: Pronoun;
  scores?: InviteeColorScores;
  profile: InviteeProfile | null;
  learner?: {
    id?: string;
    fullName?: string;
    emailAddress?: string;
  };
}

export interface InviteePayload {
  id: string;
  email: string;
  fullName: string;
  status: InviteeListOptions;
  chapters: ChaptersStatus[];
  dialect: Dialects;
  lastInviteAt?: string;
  profileLastSentAt?: string;
  leadingColourEnergy?: JungianColours;
  evaluation?: EvaluationStatus;
  pronoun?: Pronoun;
  scores?: InviteeColorScores;
  profile: InviteeProfile | null;
  profileReusePreference?: ProfileReuseStatus;
}

export type DfcInviteeResponse = Omit<InviteeResponse, 'dialect'>;

export interface InviteeRequest {
  email: string;
  fullName: string;
}

export interface UserInvite {
  emailAddress: string;
  sentAt: string;
  expiresAt: string;
}

export interface SignUpRequest {
  fullName: string;
  password: string;
  preferredDialect: string;
  token: string;
}

export type FederatedSignUpRequest = {
  fullName: string;
  preferredDialect: string;
  token: string;
};

export type FederatedSignUpResponse = {
  url: string;
};

export interface SignInRequest {
  emailAddress: string;
  password: string;
}

export interface InitiateSignInRequest {
  emailAddress: string;
}

export type InitiateSignInResponse =
  | {
      method: 'EXTERNAL';
      url: string;
    }
  | {
      method: 'PASSWORD';
      url: null;
    };

export interface ForgottenPasswordData {
  emailAddress: string;
}

export interface PasswordUpdateRequest {
  oldPassword: string;
  newPassword: string;
  dialect?: string;
}

export interface PractitionerData {
  fullName: string;
  emailAddress: string;
  id: string;
  leadingColourEnergy: JungianColours | null;
  licensedProducts: LicensedProduct[];
  role: Role;
  enabled: boolean;
  preferredDialect?: PreferredDialect | Dialects;
}

export interface PractitionerSummaryData {
  fullName: string;
  emailAddress: string;
  id: string;
  leadingColourEnergy?: JungianColours;
  createdAt: string;
  totalEvents: number;
  role: Role;
  enabled: boolean;
}

export const COLOUR_SCORE_COLOURS = ['red', 'blue', 'green', 'yellow'] as const;

export type ColourScoreColour = (typeof COLOUR_SCORE_COLOURS)[number];

export type ColourScore = Record<Colour, number>;

export interface ColourScoreWithPreference extends ColourScore {
  preferred: Colour[];
}

export type WheelPositions = {
  conscious: number;
  lessConscious: number;
  opposite: number;
};

export interface Score {
  id: string;
  fullName: string;
  wheelPositions: WheelPositions;
  colourScores: {
    conscious: ColourScoreWithPreference;
    lessConscious: ColourScoreWithPreference;
    opposite: ColourScoreWithPreference;
  };
  organisation?: { id: string; name: string };
  sortScoreArrays?: number[][];
}

export interface OrganisationGroup {
  id: string;
  name: string;
  type: GroupType;
}

export interface OrganisationResponse extends MutationResponse {
  name: string;
  learnerCount?: number;
  dataRetentionPolicy: {
    maximumInactivityPeriod: string;
  };
  visibility?: OrganisationVisibility;
  organisationGroup?: OrganisationGroup;
  canRevokeAccess?: boolean;
  group?: Group;
}

export type PagedOrganisationResponse = Pick<
  OrganisationResponse,
  'id' | 'name' | 'dataRetentionPolicy' | 'visibility' | 'organisationGroup'
> & {};

export interface SupportedRolesResponse {
  supportedRoles: Role[];
}

export interface PractitionerRequest {
  fullName: string;
  emailAddress: string;
  leadingColourEnergy?: JungianColours;
  preferredDialect?: PreferredDialect | Dialects;
  licensedProducts: LicensedProduct[];
  role: Role;
}

export interface PractitionerProduct {
  name: LicensedProduct;
  chapters: ChapterCost[];
}
export interface PractitionerProductsResponse {
  products: PractitionerProduct[];
}

export interface LearnerRequest {
  fullName: string;
  knownAs: string;
  emailAddress: string;
}

export interface UpdateLearnerResponse {
  fullName: string;
  knownAs: string;
  emailAddress: string;
}

export interface LearnerResponse {
  id: string;
  fullName: string;
  knownAs: string | null;
  primaryEmail: string;
}

export interface Profile {
  id: string;
  createdAt: string;
  chapters: ChapterName[];
  profileType?: string;
}

export interface LearnerEvaluation {
  id: string;
  submittedOn: string;
  latestProfile: Profile | null;
  latestProfiles: Profile[] | [];
  pronoun?: Pronoun;
  preferredProfileDialect: Dialects;
  archivedProfiles?: Profile[] | [];
  evaluatorType: Evaluator;
}

export interface LearnerAndInviteeResponse extends LearnerResponse {
  preferredDialect: Dialects;
  latestProfile?: Omit<Profile, 'chapters'> & {
    profileType?: ProfileType;
    evaluation: LearnerAndInviteeResponse['latestEvaluation'];
  };
  colourScores?: {
    conscious: Record<ColourScoreColour, number> & {
      preferred: ColourScoreColour[];
    };
  } | null;
  latestEvaluation?: {
    id: string;
    submittedOn: string;
  };
  leadingColourEnergy?: JungianColours;
  organisation: OrganisationResponse;
}

export interface LearnerWithEvaluation extends LearnerResponse {
  latestEvaluation: LearnerEvaluation;
  leadingColourEnergy?: JungianColours;
}

export interface FilteredLearnerResponse {
  learners: LearnerAndInviteeResponse[];
  limitExceeded: boolean;
}

export interface ProfileResponse {
  id: string;
  colourScores: {
    conscious: ColourScore;
    lessConscious: ColourScore;
    opposite: ColourScore;
  };
  wheelPositions?: WheelPositions;
  createdOn: string;
  createdAt: string;
  pronoun?: Pronoun;
  dialect: Dialects;
  evaluationSubmittedOn: string;
  type: ProfileType;
  evaluatorType: Evaluator;
  contributorCount?: number;
  supportedDialects: Dialects[];
  contributors?: LearnerResponse[];
  evaluation: Pick<EvaluationsSearchResponse, 'id'>;
  /**
   * Optional because some migrated profiles are missing chapters
   */
  chapters?: Chapter[];
}

export interface ArchivedProfileResponse extends Omit<
  ProfileResponse,
  'contributors' | 'supportedDialects' | 'colourScores' | 'dialect'
> {
  archiveReason: ReasonToArchive;
  reasonOther: string;
  preferredProfileDialect: Dialects;
}

export interface WalletResponse {
  id: string;
  availableUnits: number;
}

interface TransactionResponseCommon {
  walletId: string;
  units: number;
  walletBalance: number;
  reference: string;
  comment?: string;
  createdBy: {
    id: string;
    fullName: string;
  };
  createdAt: string;
  recipient: {
    fullName: string;
  };
  organisation?: {
    id: string;
    name: string;
  };
}

interface CommerceTransactionResponseCommon extends TransactionResponseCommon {
  sender: { fullName: string };
}

interface CreditTransactionResponse extends TransactionResponseCommon {
  operation: 'CREDIT';
}

interface RemoveTransactionResponse extends TransactionResponseCommon {
  operation: 'REMOVE';
}

interface PurchaseTransactionResponse extends CommerceTransactionResponseCommon {
  operation: 'PURCHASE';
}

interface TransferTransactionResponse extends CommerceTransactionResponseCommon {
  operation: 'TRANSFER';
}

export type TransactionResponse =
  | CreditTransactionResponse
  | RemoveTransactionResponse
  | TransferTransactionResponse
  | PurchaseTransactionResponse;

export interface CreditUnitsRequest {
  practitionerWalletId: string;
  amount: number;
  comment: string;
  reference: string;
  organisationId: string;
}

export interface TransferPractitionerUnitsRequest {
  organisationId: string;
  practitionerId: string;
  amount: number;
  comment?: string;
  reference?: string;
  transferDirection: 'ADD' | 'RECOVER';
  emailStrategy?: 'SEND' | 'SUPPRESS';
}

export interface TransferEventUnitsRequest {
  eventId: string;
  amount: number;
  transferType: 'ADD' | 'RECOVER';
}

export interface TransferUnitsResponse {
  sourceWallet: WalletResponse;
  targetWallet: WalletResponse;
}

export interface TeamLearnerResponse {
  fullName: string;
  id: string;
  knownAs: string;
  primaryEmail: string;
  pronoun?: Pronoun;
  latestProfile?: {
    createdAt: string;
    id: string;
    leadingColourEnergy: JungianColours;
    profileType?: ProfileType;
    colourScores?: {
      conscious: ColourScoreWithPreference;
    };
    evaluation?: {
      id: string;
    };
    organisation: Pick<OrganisationResponse, 'name' | 'id'>;
  };
  latestEvaluation: {
    id: string;
    submittedOn: string;
    organisation: Pick<OrganisationResponse, 'name' | 'id'>;
  };
  preferredDialect: Dialects;
}

export type TeamType = 'SINGLE' | 'MULTIPLE';
export interface OrganisationContext {
  type: TeamType;
  organisations: OrganisationResponse[];
  distinctLearnerCount: number;
  group?: Group;
}

export type TeamVisibility = 'PRIVATE' | 'ORGANISATION';

export interface TeamResponse extends MutationResponse {
  name: string;
  learnerCount: number;
  visibility: TeamVisibility;
  organisationContext?: OrganisationContext;
  migrated: boolean;
  location: string | null;
}

export type ScheduleType =
  'ONCE' | 'WEEKLY' | 'BI_WEEKLY' | 'MONTHLY' | 'IMMEDIATE';

export interface EventReminderResponse {
  reminderAt?: string;
  schedule?: ScheduleType;
}

export interface EvaluatorLink {
  id: string;
  link: string;
  name: string;
  totalCompletedEvaluators: number;
  organisation: OrganisationResponse;
  evaluatorModel: Evaluator;
  referralCodeMandatory?: boolean;
  origin: {
    createdAt: string;
    practitioner: {
      id: string;
    };
  };
  dialect: Dialects;
  practitionerNotifications?: PractitionerNotification[];
  referralCodeRequired?: boolean;
}

export type PractitionerNotification = {
  id: string;
  fullName: string;
  emailAddress: string;
  role?: Role;
};

export interface EvaluatorLinkResponse {
  evaluatorLinks: EvaluatorLink[];
}

export interface EvaluationChapters {
  name: Chapter;
}

export interface CompletedEvaluation {
  id: string;
  learner: {
    id: string;
    fullName: string;
    emailAddress: string;
  };
  profile: {
    id: string;
    chapters: ChaptersStatus[];
    createdAt: string;
  } | null;
  colourScores: {
    conscious: Record<ColourScoreColour, number> & {
      preferred: ColourScoreColour[];
    };
  } | null;
  referralCode: string;
  createdAt: string;
}

export type ChaptersForEvaluatorLinks =
  | Exclude<
      Chapter,
      'MANAGEMENT' | 'EFFECTIVE_SELLING' | 'PERSONAL_ACHIEVEMENT' | 'INTERVIEW'
    >
  | 'EXPLORE';

export interface FlattenedCompletedEvaluation {
  id: string;
  learnerId: string;
  fullName: string;
  emailAddress: string;
  referralCode: string;
  dateCompleted: string;
  latestPurchase: string | null;
  purchased: boolean;
  chapters: ChaptersForEvaluatorLinks[];
  colourScores:
    | (Record<ColourScoreColour, number> & {
        preferred: ColourScoreColour[];
      })
    | null;
}

export interface EvaluationsSearchResponse {
  id: string;
  submittedOn: string;
  latestProfile: {
    id: string;
    createdAt: string;
    chapters: ChapterName[];
    profileType?: string;
  } | null;
  evaluatorType: Evaluator;
  latestProfiles: Profile[] | [];
  learner: LearnerResponse;
  preferredProfileDialect: Dialects;
}

export interface EvaluationsProfilesResponse {
  evaluations: EvaluationsSearchResponse[];
}

export type InviteeBulkResult =
  | { type: 'SUCCESS'; inviteeData: InviteeRequest }
  | { type: 'FAILURE'; inviteeData: InviteeRequest; error: RequestError };

export const allGroupTypes = ['CUSTOMER', 'PARTNER'] as const;
export type GroupType = (typeof allGroupTypes)[number];

export interface CreateGroupRequest {
  name: string;
  type: GroupType;
}

export interface CreateGroupResponse {
  id: string;
}

export type CreateGroupedOrganisationInput = {
  groupId: string;
  name: string;
  visibility?: OrganisationVisibility;
};

export type CreateGroupedOrganisationResponse = {
  id: string;
};

export type AddOrganisationToGroupParams = {
  groupId: string;
  organisationId: string;
};

export type UpdateGroupResponse = {
  id: string;
};

export type UpdateGroupRequest = Pick<Group, 'id' | 'name' | 'type'>;

export interface Group extends OrganisationGroup {
  dataRetentionPolicy?: {
    maximumInactivityPeriod: string;
  };
}

export interface GroupResponse extends Group {
  organisations: OrganisationResponse[];
}

export interface GetGroupResponse {
  organisationGroups: GroupResponse[];
}

export type GetContributorsInvitees = {
  id: string;
  contributorInvitees: Contributor[];
  readyToPurchase?: boolean;
}[];

export interface GetContributorsResponse {
  invitees: GetContributorsInvitees;
}

export interface AddContributorRequest {
  learnerInviteeId: string;
  fullName: string;
  email: string;
}

export interface ContactFormRequest {
  practitionerId: string;
  subject: string;
  formBody: string;
}

export type WindowLinks = {
  next: string | null;
};

export interface PageWithLinks {
  links: { next: string | null };
  totalCount?: number;
}

export type PathParamsBase = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface UsePaginatedApiOptions<
  TPage extends PageWithLinks,
  TItem,
  TQueryParams extends QueryParamsBase,
  TPathParams extends PathParamsBase,
> {
  fetchPage: (
    queryParams?: TQueryParams,
    pathParams?: TPathParams,
  ) => Promise<TPage>;
  queryKey: ReadonlyArray<unknown>;
  getItemsFromPage: (page: TPage) => TItem[] | undefined;
  queryParams?: TQueryParams;
  pathParams?: TPathParams;
  initialPageParam?: string;
  enabled?: boolean;
  staleTime?: number;
  placeholderData?: (
    previousData: InfiniteData<TPage, QueryParamsBase> | undefined,
  ) => InfiniteData<TPage, QueryParamsBase> | undefined;
}

export interface GroupResponseV2 {
  id: string;
  name: string;
  type: GroupType;
  organisationsCount: number;
}

export interface PaginatedResponse<TItem> extends PageWithLinks {
  response: TItem[];
}
